import React from 'react';
import { _get, arraysEqual } from '../utility/generic';
import type {
  ReactCalendarGroupRendererProps,
  TimelineGroupBase,
  TimelineKeys,
} from '../types/main';

export interface SidebarProps<CustomGroup extends TimelineGroupBase = TimelineGroupBase> {
  groups: CustomGroup[];
  width: number;
  height: number;
  groupHeights: number[];
  keys: TimelineKeys;
  groupRenderer?: (props: ReactCalendarGroupRendererProps<CustomGroup>) => React.ReactNode;
  isRightSidebar?: boolean;
}

function renderGroupContent<CustomGroup extends TimelineGroupBase>(
  group: CustomGroup,
  isRightSidebar: boolean,
  groupTitleKey: string,
  groupRightTitleKey: string,
  groupRenderer?: (props: ReactCalendarGroupRendererProps<CustomGroup>) => React.ReactNode,
) {
  if (groupRenderer) {
    return groupRenderer({ group, isRightSidebar });
  }
  return _get(group, isRightSidebar ? groupRightTitleKey : groupTitleKey);
}

function Sidebar<CustomGroup extends TimelineGroupBase = TimelineGroupBase>({
  groups,
  width,
  height,
  groupHeights,
  keys,
  groupRenderer,
  isRightSidebar = false,
}: SidebarProps<CustomGroup>) {
  const { groupIdKey, groupTitleKey, groupRightTitleKey } = keys;

  const sidebarStyle: React.CSSProperties = {
    width: `${width}px`,
    height: `${height}px`,
  };

  const groupsStyle: React.CSSProperties = {
    width: `${width}px`,
  };

  const groupLines = groups.map((group, index) => {
    const elementStyle: React.CSSProperties = {
      height: `${groupHeights[index]}px`,
      lineHeight: `${groupHeights[index]}px`,
    };

    return (
      <div
        key={String(_get(group, groupIdKey))}
        className={'rct-sidebar-row rct-sidebar-row-' + (index % 2 === 0 ? 'even' : 'odd')}
        style={elementStyle}
      >
        {renderGroupContent(
          group,
          isRightSidebar,
          groupTitleKey,
          groupRightTitleKey,
          groupRenderer,
        )}
      </div>
    );
  });

  return (
    <div
      className={'rct-sidebar' + (isRightSidebar ? ' rct-sidebar-right' : '')}
      style={sidebarStyle}
    >
      <div style={groupsStyle}>{groupLines}</div>
    </div>
  );
}

Sidebar.displayName = 'Sidebar';

export default Sidebar;
